/**
 * `/nodes` — Servers.
 *
 * ---------------------------------------------------------------------------
 * WHAT AN OPERATOR ACTUALLY ASKS THIS PAGE
 * ---------------------------------------------------------------------------
 * Not "list the nodes" — there are six and they never change. The questions are
 * "is anything unhealthy", "is anything about to run out of room", and "where is
 * that pod". So the page is: a six-tile strip you scan in one glance, then the
 * pods that are actually wrong, then the full inventory behind a filter.
 *
 * The previous page put all 330 pods behind a `<details>` whose summary read
 * "pods by namespace (3 namespaces)" — a control that tells you the number of
 * namespaces, which is not a question anybody has, and hides the crash-looping
 * pod that is.
 *
 * ---------------------------------------------------------------------------
 * THE REQUESTS BAR
 * ---------------------------------------------------------------------------
 * It shows a PERCENTAGE only when both halves are known. An allocatable value we
 * could not parse, or a `kube_pod_container_resource_requests` that returned
 * nothing, produces the word — never a 0 % bar, which reads as "nothing is
 * scheduled here" and would be exactly wrong. There is no bar drawn for an
 * unmeasured quantity anywhere on this page; the dotted chip takes its place,
 * so the bar rail is never empty-because-unknown.
 */

import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { formatBytes, humanizeSeconds, parseQuantity } from '../lib/format';
import { formatNumber } from '../lib/signal';
import type { Signal } from '../lib/signal';
import {
  Absent,
  Card,
  Eyebrow,
  Ident,
  KeyValue,
  Placeholder,
  SignalPanel,
  SignalValue,
  StatusPill,
} from '../components/primitives';
import { SeriesChart } from '../components/SeriesChart';

interface PodCard {
  namespace: string;
  name: string;
  node?: string | null;
  phase: string;
  reason: string | null;
  ready: boolean;
  restarts: number;
  ageSeconds: number | null;
  workloadKind: string | null;
  workloadName?: string | null;
  dbRole: string | null;
  dbCluster: string | null;
  notReadyReasons: string[];
}

interface NodeCard {
  name: string;
  ready: boolean | null;
  unschedulable: boolean;
  memoryPressure: boolean | null;
  diskPressure: boolean | null;
  pidPressure: boolean | null;
  kubeletVersion: string | null;
  ageSeconds: number | null;
  allocatable: Record<string, string>;
  capacity: Record<string, string>;
  requests: Signal<Record<string, number>>;
  podCount: number;
  restartsTotal: number;
  podsByNamespace: Record<string, PodCard[]>;
}

export interface NodesData {
  generatedAt: string;
  nodes: Signal<NodeCard[]>;
  pods: Signal<{ total: number }>;
  storage: Signal<Record<string, number>>;
  diskIo: Signal<Record<string, number>>;
}

/* ------------------------------------------------------------------ *
 * Requests
 * ------------------------------------------------------------------ */

interface Utilisation {
  kind: 'ok';
  requested: number;
  allocatable: number;
  percent: number;
}

type UtilisationResult =
  | Utilisation
  | { kind: 'signal' }
  | { kind: 'no-series' }
  | { kind: 'unparseable'; requested: number; raw: string | undefined };

function utilisationOf(node: NodeCard, resource: string): UtilisationResult {
  if (node.requests.status !== 'ok') return { kind: 'signal' };
  const requested = node.requests.value?.[resource];
  if (requested === undefined) return { kind: 'no-series' };
  const raw = node.allocatable[resource];
  const allocatable = raw === undefined ? null : parseQuantity(raw);
  if (allocatable === null || allocatable === 0) {
    return { kind: 'unparseable', requested, raw };
  }
  return { kind: 'ok', requested, allocatable, percent: (requested / allocatable) * 100 };
}

/**
 * The colour a utilisation percentage earns.
 *
 * A node at 30 % is not news and is drawn in ink. Amber starts at 75 % and red
 * at 90 % — the two levels where an operator would actually do something. This
 * is the scarcity rule applied to a bar: six green bars on a healthy page train
 * the eye to ignore the bar rail entirely, and then the one at 91 % is missed.
 */
function utilTone(percent: number): 'good' | 'warn' | 'bad' {
  return percent >= 90 ? 'bad' : percent >= 75 ? 'warn' : 'good';
}

/** Bar fill: ink until it matters. */
function utilBarClass(tone: 'good' | 'warn' | 'bad'): string {
  return tone === 'bad' ? 'bg-bad' : tone === 'warn' ? 'bg-warn' : 'bg-ink-3';
}

/** Percentage text: ink until it matters. */
function utilTextClass(tone: 'good' | 'warn' | 'bad'): string {
  return tone === 'bad' ? 'text-bad' : tone === 'warn' ? 'text-warn' : 'text-ink-2';
}

function RequestsRow({
  node,
  resource,
  label,
  format,
}: {
  node: NodeCard;
  resource: string;
  label: string;
  format: (value: number) => string;
}): ReactNode {
  const util = utilisationOf(node, resource);

  if (util.kind === 'signal') {
    return (
      <KeyValue label={label}>
        <SignalValue signal={node.requests} />
      </KeyValue>
    );
  }
  if (util.kind === 'no-series') {
    return (
      <KeyValue label={label}>
        <Absent
          title={`no ${resource} request series for this node — that is not a request of zero`}
        >
          not measured
        </Absent>
      </KeyValue>
    );
  }
  if (util.kind === 'unparseable') {
    return (
      <KeyValue label={label}>
        <Absent
          title={`allocatable ${resource} is ${util.raw ?? 'absent'} and could not be parsed, so a percentage would be invented`}
        >
          {format(util.requested)} of unknown
        </Absent>
      </KeyValue>
    );
  }

  const tone = utilTone(util.percent);
  return (
    <div className="py-2">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-xs text-ink-3">{label}</span>
        <span className="num text-sm text-ink">
          {format(util.requested)}{' '}
          <span className="text-ink-3">/ {format(util.allocatable)}</span>{' '}
          <span className={`font-medium ${utilTextClass(tone)}`}>
            {formatNumber(util.percent, 0)}%
          </span>
        </span>
      </div>
      {/* The bar duplicates the number above; it never carries it alone. */}
      <div
        className="mt-2 h-1 overflow-hidden rounded-full bg-white/7"
        role="img"
        aria-label={`${label}: ${format(util.requested)} requested of ${format(util.allocatable)} allocatable, ${formatNumber(util.percent, 0)} percent`}
      >
        <div
          className={`h-full rounded-full ${utilBarClass(tone)}`}
          style={{ width: `${String(Math.min(100, util.percent))}%` }}
        />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Pods
 * ------------------------------------------------------------------ */

function RoleChip({ role, cluster }: { role: string | null; cluster: string | null }): ReactNode {
  if (role === null) return null;
  /*
   * Ink, not hue. `primary` is the one an operator hunts for during a failover,
   * so it gets the FILLED container and full-strength ink; `replica` gets the
   * same container a step dimmer; `unknown-role` gets the dotted one, because a
   * cluster reporting no primary is a "we do not know", not a third role.
   */
  const style =
    role === 'primary'
      ? 'epi-measured text-ink'
      : role === 'replica'
        ? 'epi-measured text-ink-3'
        : 'epi-unmeasured text-ink-3';
  return (
    <span
      className={`shrink-0 rounded border px-1.5 text-[10px] font-medium uppercase tracking-wide ${style}`}
      title={cluster ?? undefined}
    >
      {role === 'unknown-role' ? 'unknown role' : role}
    </span>
  );
}

/**
 * One pod.
 *
 * Two lines, not one flex row. A single row with `flex-1 break-all` on the name
 * and `shrink-0` chips beside it collapsed the name to a four-character column
 * at 390 px and rendered `horeca-worker-5c4b2` as five stacked fragments. Pod
 * names are the identifier you act on; they get the full width of their own
 * line and the metadata sits underneath.
 */
function PodRow({ pod, showNode = false }: { pod: PodCard; showNode?: boolean }): ReactNode {
  const chips: ReactNode[] = [];
  if (showNode && pod.node != null) {
    chips.push(
      <span key="node" className="text-[10px] text-ink-3">
        {pod.node}
      </span>,
    );
  }
  if (pod.dbRole !== null) {
    chips.push(<RoleChip key="role" role={pod.dbRole} cluster={pod.dbCluster} />);
  }
  if (pod.restarts > 0) {
    chips.push(
      <span
        key="restarts"
        className="epi-measured num rounded border px-1.5 text-[10px] text-warn"
        title={`${String(pod.restarts)} container restarts`}
      >
        {pod.restarts}× restart
      </span>,
    );
  }

  return (
    <li className="rounded-lg bg-surface-2 px-2.5 py-2">
      <div className="flex items-start justify-between gap-2">
        <Ident className="min-w-0 break-all leading-snug">{pod.name}</Ident>
        <span className="shrink-0">
          <StatusPill
            status={pod.ready ? 'ok' : 'error'}
            size="sm"
            title={pod.notReadyReasons.length > 0 ? pod.notReadyReasons.join('; ') : undefined}
          >
            {pod.ready ? 'ready' : (pod.reason ?? pod.phase)}
          </StatusPill>
        </span>
      </div>
      {chips.length > 0 && (
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">{chips}</div>
      )}
    </li>
  );
}

/* ------------------------------------------------------------------ *
 * Page
 * ------------------------------------------------------------------ */

export function NodesPage({ data }: { data: NodesData | null }): ReactNode {
  const [query, setQuery] = useState('');

  const nodes = data !== null && data.nodes.status === 'ok' ? (data.nodes.value ?? []) : [];

  const allPods = useMemo(
    () =>
      nodes.flatMap((node) =>
        Object.values(node.podsByNamespace)
          .flat()
          .map((pod) => ({ ...pod, node: pod.node ?? node.name })),
      ),
    [nodes],
  );

  /** Pods worth looking at: not ready, or restarting. */
  const attention = useMemo(
    () => allPods.filter((pod) => !pod.ready || pod.restarts > 0),
    [allPods],
  );

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (needle === '') return [];
    return allPods
      .filter(
        (pod) =>
          pod.name.toLowerCase().includes(needle) ||
          pod.namespace.toLowerCase().includes(needle) ||
          (pod.node ?? '').toLowerCase().includes(needle),
      )
      .slice(0, 120);
  }, [allPods, query]);

  if (data === null) {
    return (
      <div className="space-y-3" role="status" aria-label="loading servers">
        <div className="h-32 animate-pulse rounded-card border border-line bg-surface" />
        <div className="h-64 animate-pulse rounded-card border border-line bg-surface" />
      </div>
    );
  }

  return (
    <div className="space-y-3 md:space-y-4">
      {data.pods.status !== 'ok' && (
        <Placeholder status={data.pods.status} title="pods could not be listed">
          The node cards below therefore show no pods. That is not the same as a node with no pods
          on it.
        </Placeholder>
      )}

      {/* The strip. Six tiles, one glance. */}
      <SignalPanel signal={data.nodes} emptyTitle="the node list could not be read">
        {(list) => (
          <ul className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 xl:grid-cols-3">
            {list.map((node) => (
              <li key={node.name}>
                <NodeTile node={node} storage={data.storage} />
              </li>
            ))}
          </ul>
        )}
      </SignalPanel>

      {/* What is wrong, across every node. */}
      <Card
        title="Pods needing attention"
        icon="errors"
        subtitle="not ready, or with container restarts"
        right={
          <StatusPill status={attention.length === 0 ? 'ok' : 'error'}>
            {attention.length === 0 ? 'none' : `${String(attention.length)}`}
          </StatusPill>
        }
      >
        {data.pods.status !== 'ok' ? (
          <Placeholder status={data.pods.status} title="the pod list was not read">
            This panel is empty because nothing was listed, not because nothing is wrong.
          </Placeholder>
        ) : attention.length === 0 ? (
          <p className="text-xs text-ink-3">
            Every pod the collector listed is ready and has zero restarts.
          </p>
        ) : (
          <ul className="space-y-1.5">
            {attention.map((pod) => (
              <PodRow key={`${pod.namespace}/${pod.name}`} pod={pod} showNode />
            ))}
          </ul>
        )}
      </Card>

      {/* The inventory, reachable by name. */}
      <Card
        title="Pod inventory"
        icon="search"
        subtitle={`${String(allPods.length)} pods listed`}
      >
        <label htmlFor="pod-search" className="sr-only">
          search pods by name, namespace or node
        </label>
        <input
          id="pod-search"
          type="search"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
          }}
          placeholder="e.g. mount-pg, w-cloud-1, delivery"
          className="w-full rounded-lg border border-line bg-surface-2 px-3.5 text-sm text-ink placeholder:text-ink-3"
        />
        {query.trim() !== '' && (
          <div className="mt-3">
            <Eyebrow>
              {filtered.length === 0
                ? 'no pods match'
                : `${String(filtered.length)} ${filtered.length === 1 ? 'match' : 'matches'}`}
            </Eyebrow>
            <ul className="mt-2 space-y-1.5">
              {filtered.map((pod) => (
                <PodRow key={`${pod.namespace}/${pod.name}`} pod={pod} showNode />
              ))}
            </ul>
          </div>
        )}
      </Card>

      {/* Per-node detail. */}
      <SignalPanel signal={data.nodes} emptyTitle="the node list could not be read">
        {(list) => (
          <div className="space-y-3">
            {list.map((node) => (
              <NodeDetail key={node.name} node={node} podsListed={data.pods.status === 'ok'} />
            ))}
          </div>
        )}
      </SignalPanel>

      <Card
        title="Storage"
        icon="storage"
        subtitle="topolvm volume groups — free bytes per node"
        right={<StatusPill status={data.storage.status} />}
      >
        <SignalPanel signal={data.storage} emptyTitle="storage free space is not observable">
          {(byNode) => (
            <dl className="divide-y divide-line">
              {Object.entries(byNode).map(([node, bytes]) => (
                <KeyValue key={node} label={node}>
                  <span className="num">{formatBytes(bytes)}</span>
                </KeyValue>
              ))}
            </dl>
          )}
        </SignalPanel>
      </Card>

      <div className="grid gap-3 xl:grid-cols-2">
        <SeriesChart name="node_disk_io" title="Disk IO saturation" defaultRange="6h" maxSeries={6} />
        <SeriesChart name="node_allocatable" title="Allocatable per node" defaultRange="24h" maxSeries={8} />
      </div>
    </div>
  );
}

function NodeTile({
  node,
  storage,
}: {
  node: NodeCard;
  storage: Signal<Record<string, number>>;
}): ReactNode {
  const cpu = utilisationOf(node, 'cpu');
  const memory = utilisationOf(node, 'memory');
  const free = storage.status === 'ok' ? storage.value?.[node.name] : undefined;
  const pressures = [
    node.memoryPressure === true ? 'MemoryPressure' : null,
    node.diskPressure === true ? 'DiskPressure' : null,
    node.pidPressure === true ? 'PIDPressure' : null,
  ].filter((p): p is string => p !== null);

  return (
    <a href={`#node-${node.name}`} className="tap block rounded-card border border-line bg-surface p-3.5 transition-colors hover:border-line-strong hover:bg-surface-2">
      <div className="flex items-start justify-between gap-2">
        <Ident className="min-w-0 break-all text-sm font-medium leading-snug text-ink">
          {node.name}
        </Ident>
        <div className="flex shrink-0 flex-wrap justify-end gap-1">
          <StatusPill
            status={node.ready === true ? 'ok' : node.ready === false ? 'error' : 'unproven'}
            size="sm"
          >
            {node.ready === true ? 'Ready' : node.ready === false ? 'NotReady' : 'unknown'}
          </StatusPill>
          {node.unschedulable && (
            <StatusPill status="unproven" size="sm">
              cordoned
            </StatusPill>
          )}
        </div>
      </div>

      {pressures.length > 0 && (
        <ul className="mt-1.5 flex flex-wrap gap-1">
          {pressures.map((pressure) => (
            <li key={pressure}>
              <StatusPill status="error" size="sm">
                {pressure}
              </StatusPill>
            </li>
          ))}
        </ul>
      )}

      <dl className="mt-2.5 space-y-1.5">
        <TileBar label="cpu" util={cpu} format={(v) => `${formatNumber(v, 1)}`} suffix="cores" />
        <TileBar label="mem" util={memory} format={formatBytes} />
      </dl>

      <p className="num mt-2.5 flex flex-wrap gap-x-3 text-2xs text-ink-3">
        <span>
          <span className="text-ink-3">pods </span>
          {node.podCount}
        </span>
        <span>
          <span className="text-ink-3">restarts </span>
          <span className={node.restartsTotal > 0 ? 'text-warn' : ''}>{node.restartsTotal}</span>
        </span>
        <span>
          <span className="text-ink-3">up </span>
          {humanizeSeconds(node.ageSeconds)}
        </span>
        {free !== undefined && (
          <span>
            <span className="text-ink-3">free </span>
            {formatBytes(free)}
          </span>
        )}
      </p>
    </a>
  );
}

/**
 * The compact bar inside a node tile.
 *
 * When the utilisation is not measurable it renders the WORD in a dotted chip
 * and draws no track at all — an empty grey track next to a full one reads as
 * "0 %", which is the single most dangerous misreading on this page.
 */
function TileBar({
  label,
  util,
  format,
  suffix,
}: {
  label: string;
  util: UtilisationResult;
  format: (value: number) => string;
  suffix?: string;
}): ReactNode {
  if (util.kind !== 'ok') {
    return (
      <div className="flex items-baseline justify-between gap-2">
        <dt className="text-2xs uppercase tracking-[0.08em] text-ink-3">{label}</dt>
        <dd>
          <Absent size="sm">
            {util.kind === 'unparseable' ? 'allocatable unparseable' : 'not measured'}
          </Absent>
        </dd>
      </div>
    );
  }
  const tone = utilTone(util.percent);
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2">
        <dt className="text-2xs uppercase tracking-[0.08em] text-ink-3">{label}</dt>
        <dd className="num text-2xs text-ink-3">
          {format(util.requested)}
          {suffix === undefined ? '' : ` ${suffix}`}{' '}
          <span className={`font-medium ${utilTextClass(tone)}`}>
            {formatNumber(util.percent, 0)}%
          </span>
        </dd>
      </div>
      <div
        className="mt-1.5 h-1 overflow-hidden rounded-full bg-white/7"
        role="img"
        aria-label={`${label} requested ${format(util.requested)} of ${format(util.allocatable)}, ${formatNumber(util.percent, 0)} percent`}
      >
        <div
          className={`h-full rounded-full ${utilBarClass(tone)}`}
          style={{ width: `${String(Math.min(100, util.percent))}%` }}
        />
      </div>
    </div>
  );
}

function NodeDetail({ node, podsListed }: { node: NodeCard; podsListed: boolean }): ReactNode {
  const namespaces = Object.entries(node.podsByNamespace).sort(([a], [b]) => a.localeCompare(b));
  return (
    <Card
      id={`node-${node.name}`}
      title={<Ident className="text-md font-semibold text-ink">{node.name}</Ident>}
      subtitle={`${String(node.podCount)} pods · ${String(node.restartsTotal)} restarts · up ${humanizeSeconds(node.ageSeconds)} · ${node.kubeletVersion ?? 'unknown kubelet'}`}
      right={
        <StatusPill status={node.ready === true ? 'ok' : node.ready === false ? 'error' : 'unproven'}>
          {node.ready === true ? 'Ready' : node.ready === false ? 'NotReady' : 'unknown'}
        </StatusPill>
      }
    >
      <div className="space-y-0.5">
        <RequestsRow
          node={node}
          resource="cpu"
          label="CPU requested"
          format={(v) => `${formatNumber(v, 2)} cores`}
        />
        <RequestsRow node={node} resource="memory" label="memory requested" format={formatBytes} />
      </div>

      <div className="mt-3 border-t border-line pt-3">
        {!podsListed ? (
          <p className="text-xs text-warn">
            The pod list was not read this cycle, so this node shows no pods. That is not an empty
            node.
          </p>
        ) : namespaces.length === 0 ? (
          <p className="text-xs text-ink-3">No pods were listed on this node.</p>
        ) : (
          <div className="space-y-3">
            {namespaces.map(([namespace, pods]) => (
              <div key={namespace}>
                <Eyebrow>
                  {namespace} · {pods.length}
                </Eyebrow>
                <ul className="mt-1.5 space-y-1.5">
                  {pods.map((pod) => (
                    <PodRow key={pod.name} pod={pod} />
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}
